// Contrail V3.4.0 — zone tool: list, show/hide, draw and edit zones (geofences) on the main map; export as Shapefile / GeoJSON / KML.
// V3.4.0: every polygon corner can be dragged; "+" handles between corners insert a new one; select a corner and press
// Delete (or the button) to remove it. Circles: drag the centre or the radius handle. Free colour picker with remembered colours.
// Global zones: Skyward staff manage them, every user sees them. Company zones: staff + the company owner manage them,
// everyone in the company sees them.
import { useEffect, useMemo, useRef, useState } from "react";
import { LngLatBounds, Marker, type GeoJSONSource, type Map as MLMap, type MapMouseEvent } from "maplibre-gl";
import { supabase } from "../../lib/supabase";
import { orgApi, type Zone } from "../../lib/org";
import { DISPLAY_COLOURS } from "../../lib/icons";
import { areaM2, circle, fmtArea, metres, zoneFeature, zoneSize, type LngLat } from "../../lib/geo";
import { download, exportZones, type ExportFormat } from "../../lib/zoneExport";
import { previewFeature, readZoneFile, type ImportZone } from "../../lib/zoneImport";
import ColourPicker, { rememberColour } from "../../ui/ColourPicker";
import { ToolPanel } from "./MapTools";
import { dragHandle } from "../../lib/dragHandle";

type Managed = Set<string> | "all" | null;
type Draft = { id?: string; name: string; scope: "global" | "company" | "multi"; org_id: string; org_ids?: string[]; kind: "circle" | "polygon";
  center: LngLat | null; radius: number; polygon: LngLat[]; color: string };

export default function ZoneTool({ map, zones, reload, staff, admin = false, managed, show, setShow, onClose }: {
  map: MLMap; zones: Zone[]; reload: () => Promise<void> | void; staff: boolean; admin?: boolean; managed: Managed;
  show: boolean; setShow: (v: boolean) => void; onClose: () => void;
}) {
  const [orgs, setOrgs] = useState<{ id: string; name: string; type: string }[]>([]);
  const [q, setQ] = useState("");
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);   // V3.12.0
  const [note, setNote] = useState<string | null>(null);
  useEffect(() => { supabase.from("organisations").select("id, name, type").order("name").then(({ data }) => setOrgs(data ?? [])); }, []);

  const canManageOrg = (org: string) => managed === "all" || (managed instanceof Set && managed.has(org));
  const canEdit = (z: Zone) => (z.is_global ? staff : canManageOrg(z.org_id));
  const companyChoices = orgs.filter((o) => canManageOrg(o.id));
  const canCreate = staff || companyChoices.length > 0;
  const orgName = (id: string) => orgs.find((o) => o.id === id)?.name ?? "Company";

  const groups = useMemo(() => {
    const s = q.trim().toLowerCase();
    const list = zones.filter((z) => !s || z.name.toLowerCase().includes(s));
    const global = list.filter((z) => z.is_global);
    const byOrg = new Map<string, Zone[]>();
    for (const z of list.filter((z) => !z.is_global)) byOrg.set(z.org_id, [...(byOrg.get(z.org_id) ?? []), z]);
    return { global, byOrg: [...byOrg.entries()].sort((a, b) => orgName(a[0]).localeCompare(orgName(b[0]))) };
  }, [zones, q, orgs]); // eslint-disable-line

  function flyTo(z: Zone) {
    const f = zoneFeature(z);
    const ring = f.geometry.coordinates[0] as LngLat[];
    const b = new LngLatBounds(ring[0], ring[0]);
    ring.forEach((p) => b.extend(p));
    map.fitBounds(b, { padding: 80, maxZoom: 16, duration: 600 });
  }
  function startNew() {
    const c = map.getCenter();
    const defOrg = companyChoices.find((o) => o.type !== "internal")?.id ?? companyChoices[0]?.id ?? "";
    setErr(null);
    setDraft({ name: "", scope: staff ? "global" : "company", org_id: defOrg, kind: "circle",
      center: [c.lng, c.lat], radius: 300, polygon: [], color: DISPLAY_COLOURS[0] });
  }
  function startEdit(z: Zone) {
    setErr(null);
    setDraft({ id: z.id, name: z.name, scope: z.is_global ? "global" : "company", org_id: z.org_id, kind: z.kind,
      center: [z.center_lng, z.center_lat], radius: z.radius_m ?? 300, polygon: (z.polygon ?? []) as LngLat[], color: z.color });
    flyTo(z);
  }
  const [fmt, setFmt] = useState<ExportFormat>(() => { try { return (localStorage.getItem("fw.zones.fmt") as ExportFormat) || "shp"; } catch { return "shp"; } });
  useEffect(() => { try { localStorage.setItem("fw.zones.fmt", fmt); } catch { /* ignore */ } }, [fmt]);
  function exportSet(list: Zone[], label: string) {
    if (!list.length) return;
    const rows = list.map((z) => ({ ...z, company: z.is_global ? "Global" : orgName(z.org_id) }));
    const { blob, filename } = exportZones(rows, fmt, label);
    download(blob, filename);
  }
  const dl = (list: Zone[], label: string, title: string) => (
    <button type="button" className="zt-dl" onClick={() => exportSet(list, label)} title={title} aria-label={title}>
      <svg viewBox="0 0 16 16"><path d="M8 2v8M4.5 6.5 8 10l3.5-3.5M3 13h10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
    </button>
  );

  async function remove(z: Zone) {
    if (!confirm(`Delete zone ${z.name}?`)) return;
    try { await orgApi("DELETE", `geofences/${z.id}`); await reload(); } catch (e: any) { setErr(e.message); }
  }
  async function save() {
    if (!draft) return;
    if (!draft.name.trim()) return setErr("Give the zone a name.");
    if (draft.kind === "circle" && !draft.center) return setErr("Click the map to place the zone.");
    if (draft.kind === "polygon" && draft.polygon.length < 3) return setErr("Click at least 3 points on the map.");
    setBusy(true); setErr(null);
    const body: Record<string, unknown> = { name: draft.name.trim(), color: draft.color, kind: draft.kind,
      ...(draft.kind === "circle" ? { center_lat: draft.center![1], center_lng: draft.center![0], radius_m: draft.radius } : { polygon: draft.polygon }) };
    if (!draft.id && draft.scope !== "multi") Object.assign(body, draft.scope === "global" ? { scope: "global" } : { org_id: draft.org_id });
    if (draft.scope === "multi" && !(draft.org_ids ?? []).length) { setBusy(false); return setErr("Tick at least one company."); }
    try {
      if (draft.id) await orgApi("PATCH", `geofences/${draft.id}`, body);
      else if (draft.scope === "multi") {   // V3.12.0: one copy per company (admins)
        const r = await orgApi<BulkResult>("POST", "geofences/bulk", { targets: { org_ids: draft.org_ids }, zones: [body] });
        setNote(bulkText(r));
      } else await orgApi("POST", "geofences", body);
      rememberColour(draft.color);
      await reload(); setDraft(null);
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  }

  if (importing) return <ZoneImport map={map} orgs={orgs.filter((o) => o.type !== "internal")}
    onCancel={() => setImporting(false)} onDone={async (r) => { setImporting(false); setNote(bulkText(r)); await reload(); }} />;
  if (draft) return <ZoneDraw map={map} draft={draft} setDraft={setDraft} busy={busy} err={err} staff={staff} admin={admin}
    allCompanies={orgs.filter((o) => o.type !== "internal")}
    companies={companyChoices} usedColours={[...new Set(zones.map((z) => z.color))]} onSave={save} onCancel={() => { setDraft(null); setErr(null); }} />;

  const row = (z: Zone) => (
    <li key={z.id}>
      <button className="zt-name" onClick={() => flyTo(z)} title="Show on map">
        <i className={`zone-dot ${z.is_global ? "global" : ""}`} style={{ background: z.color }} />
        <span><b>{z.name}</b><span className="sub">{zoneSize(z)}</span></span>
      </button>
      <span className="zt-act">
        {dl([z], z.name, `Download ${z.name}`)}
        {canEdit(z) && <>
        <button className="btn-ghost small" onClick={() => startEdit(z)}>Edit</button>
        <button className="btn-ghost small danger" onClick={() => remove(z)} aria-label={`Delete ${z.name}`}>✕</button>
        </>}
      </span>
    </li>
  );

  return (
    <ToolPanel title="Zones" onClose={onClose} footer={canCreate ? <>
      {admin && <button className="btn-ghost small" onClick={() => { setNote(null); setImporting(true); }} title="Add zones from a KML, KMZ, GeoJSON or Shapefile">Import…</button>}
      <button className="btn-primary small" onClick={startNew}>+ New zone</button></> : undefined}>
      {note && <div className="zt-note" role="status">{note}<button type="button" className="x" aria-label="Dismiss" onClick={() => setNote(null)}>×</button></div>}
      <label className="zt-show"><input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} /> Show zones on the map</label>
      {zones.length > 6 && <input className="zt-search" placeholder="Search zones" value={q} onChange={(e) => setQ(e.target.value)} />}
      {err && <div className="error small">{err}</div>}
      <div className="zt-export">
        <span className="sub">Download as</span>
        <select value={fmt} onChange={(e) => setFmt(e.target.value as ExportFormat)} aria-label="Download format">
          <option value="shp">Shapefile (.zip)</option><option value="geojson">GeoJSON</option><option value="kml">KML (Google Earth)</option>
        </select>
        <button type="button" className="btn-ghost small" disabled={!zones.length} onClick={() => exportSet(zones, "All")}>All zones</button>
      </div>
      <div className="zt-list">
        <h4><span className="zt-h">Global zones <span className="sub">· everyone</span></span>{groups.global.length > 0 && dl(groups.global, "Global", "Download all global zones")}</h4>
        <ul>{groups.global.map(row)}{!groups.global.length && <li className="sub">None yet{staff ? " — add ports, airports, depots or no-go areas for every client." : "."}</li>}</ul>
        {groups.byOrg.map(([org, list]) => (
          <div key={org}><h4><span className="zt-h" title={orgName(org)}>{orgName(org)} <span className="sub">· company</span></span>{dl(list, orgName(org), `Download all ${orgName(org)} zones`)}</h4><ul>{list.map(row)}</ul></div>
        ))}
        {!groups.byOrg.length && <><h4>Company zones</h4><p className="sub">No company zones yet.</p></>}
      </div>
    </ToolPanel>
  );
}

function ZoneDraw({ map, draft, setDraft, busy, err, staff, admin = false, allCompanies = [], companies, usedColours, onSave, onCancel }: {
  map: MLMap; draft: Draft; setDraft: (d: Draft) => void; busy: boolean; err: string | null; staff: boolean; admin?: boolean;
  allCompanies?: { id: string; name: string }[];
  companies: { id: string; name: string }[]; usedColours: string[]; onSave: () => void; onCancel: () => void;
}) {
  const [sel, setSel] = useState<number | null>(null);
  const selRef = useRef(sel); selRef.current = sel;
  const live = useRef(draft); live.current = draft;
  const handles = useRef<Marker[]>([]);

  // fill + outline of the shape being drawn
  useEffect(() => {
    const empty = { type: "FeatureCollection" as const, features: [] };
    if (!map.getSource("tf-zdraw")) map.addSource("tf-zdraw", { type: "geojson", data: empty });
    map.addLayer({ id: "tf-zdraw-fill", type: "fill", source: "tf-zdraw", filter: ["==", "$type", "Polygon"], paint: { "fill-color": draft.color, "fill-opacity": 0.18 } });
    map.addLayer({ id: "tf-zdraw-line", type: "line", source: "tf-zdraw", filter: ["!=", "$type", "Point"], paint: { "line-color": draft.color, "line-width": 2.5 } });
    map.getCanvas().style.cursor = "crosshair";
    map.doubleClickZoom.disable();
    return () => {
      handles.current.forEach((m) => m.remove()); handles.current = [];
      for (const id of ["tf-zdraw-line", "tf-zdraw-fill"]) if (map.getLayer(id)) map.removeLayer(id);
      if (map.getSource("tf-zdraw")) map.removeSource("tf-zdraw");
      map.getCanvas().style.cursor = "";
      map.doubleClickZoom.enable();
    };
  }, [map]); // eslint-disable-line

  // draw the outline/fill straight onto the map (also used live while a handle is being dragged)
  const paint = (d: Draft) => {
    for (const [id, prop] of [["tf-zdraw-fill", "fill-color"], ["tf-zdraw-line", "line-color"]] as const)
      if (map.getLayer(id)) map.setPaintProperty(id, prop, d.color);
    const features: any[] = [];
    if (d.kind === "circle" && d.center) features.push(circle(d.center, d.radius));
    if (d.kind === "polygon") {
      const p = d.polygon;
      if (p.length >= 3) features.push({ type: "Feature", properties: {}, geometry: { type: "Polygon", coordinates: [[...p, p[0]]] } });
      else if (p.length === 2) features.push({ type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: p } });
    }
    (map.getSource("tf-zdraw") as GeoJSONSource | undefined)?.setData({ type: "FeatureCollection", features });
  };
  useEffect(() => { paint(draft); }, [map, draft]); // eslint-disable-line

  // Draggable handles: polygon corners (+ "+" midpoints to insert), circle centre + radius.
  // V3.4.2: handles use pointer capture (lib/dragHandle) instead of MapLibre's draggable markers, which stall whenever
  // the map's own gestures/camera are busy. While dragging only the outline is redrawn; the shape is saved on release.
  // Handles are re-created only when the saved shape changes — selecting a corner or changing colour just restyles them.
  const shapeKey = draft.kind === "circle" ? `c:${draft.center?.join()}:${draft.radius}` : `p:${draft.polygon.map((p) => p.join()).join(";")}`;
  const cornerEls = useRef<HTMLElement[]>([]);
  useEffect(() => {
    handles.current.forEach((m) => m.remove()); handles.current = []; cornerEls.current = [];
    const box = map.getContainer();
    const round = (l: { lng: number; lat: number }): LngLat => [+l.lng.toFixed(6), +l.lat.toFixed(6)];
    const mk = (cls: string, at: LngLat, opts: { preview: (base: Draft, p: LngLat) => Draft; done?: () => void; click?: () => void; title?: string }) => {
      const el = document.createElement("div");
      el.className = `zh ${cls}`; if (opts.title) el.title = opts.title;
      el.style.setProperty("--c", live.current.color);
      const m = new Marker({ element: el }).setLngLat(at).addTo(map);
      let base: Draft = live.current;
      dragHandle(map, m, {
        onStart: () => { base = live.current; box.classList.add("zh-dragging"); el.classList.add("dragging"); },
        onMove: (ll) => { live.current = opts.preview(base, round(ll)); paint(live.current); },
        onEnd: (ll) => {
          box.classList.remove("zh-dragging"); el.classList.remove("dragging");
          live.current = opts.preview(base, round(ll));
          setDraft(live.current); opts.done?.();
        },
        onClick: opts.click,
      });
      handles.current.push(m);
      return el;
    };
    const d = draft;
    if (d.kind === "circle" && d.center) {
      const c = d.center;
      mk("centre", c, { title: "Drag to move the zone", preview: (b, p) => ({ ...b, center: p }) });
      const dLng = d.radius / (111320 * Math.cos((c[1] * Math.PI) / 180));
      mk("radius", [c[0] + dLng, c[1]], { title: "Drag to change the radius",
        preview: (b, p) => ({ ...b, radius: Math.max(20, Math.min(100000, Math.round(metres(b.center!, p)))) }) });
    }
    if (d.kind === "polygon") {
      const p = d.polygon;
      cornerEls.current = p.map((pt, i) => mk(`corner ${selRef.current === i ? "sel" : ""}`, pt, { title: "Drag to move · click to select",
        click: () => setSel(i),
        preview: (b, np) => { const q = [...b.polygon]; q[i] = np; return { ...b, polygon: q }; } }));
      if (p.length >= 2) {
        const n = p.length >= 3 ? p.length : p.length - 1;
        for (let i = 0; i < n; i++) {
          const a = p[i], b2 = p[(i + 1) % p.length];
          const mid: LngLat = [+((a[0] + b2[0]) / 2).toFixed(6), +((a[1] + b2[1]) / 2).toFixed(6)];
          mk("mid", mid, { title: "Drag or click to add a corner here",
            click: () => { const q = [...live.current.polygon]; q.splice(i + 1, 0, mid); setDraft({ ...live.current, polygon: q }); setSel(i + 1); },
            preview: (b, np) => { const q = [...b.polygon]; q.splice(i + 1, 0, np); return { ...b, polygon: q }; },
            done: () => setSel(i + 1) });
        }
      }
    }
    return () => { box.classList.remove("zh-dragging"); };
  }, [map, shapeKey, draft.kind]); // eslint-disable-line
  // selection + colour only restyle the existing handles (never re-create them)
  useEffect(() => { cornerEls.current.forEach((el, i) => el.classList.toggle("sel", sel === i)); }, [sel, shapeKey]);
  useEffect(() => { handles.current.forEach((m) => m.getElement().style.setProperty("--c", draft.color)); }, [draft.color, shapeKey]);

  const removeSel = () => {
    if (sel == null || draft.kind !== "polygon") return;
    setDraft({ ...draft, polygon: draft.polygon.filter((_, i) => i !== sel) }); setSel(null);
  };
  useEffect(() => {
    const click = (e: MapMouseEvent) => {
      const p: LngLat = [+e.lngLat.lng.toFixed(6), +e.lngLat.lat.toFixed(6)];
      const d = live.current;
      if (d.kind === "circle") { setDraft({ ...d, center: p }); return; }
      if (d.polygon.length >= 300) return;
      // add after the selected corner (or at the end)
      const q = [...d.polygon]; const at = sel != null ? sel + 1 : q.length; q.splice(at, 0, p);
      setDraft({ ...d, polygon: q }); setSel(at);
    };
    const key = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (t && /INPUT|TEXTAREA|SELECT/.test(t.tagName)) return;
      if (e.key === "Escape") { if (sel != null) setSel(null); else onCancel(); }
      if ((e.key === "Delete" || e.key === "Backspace") && live.current.kind === "polygon") {
        e.preventDefault();
        if (sel != null) removeSel(); else setDraft({ ...live.current, polygon: live.current.polygon.slice(0, -1) });
      }
    };
    map.on("click", click);
    window.addEventListener("keydown", key);
    return () => { map.off("click", click); window.removeEventListener("keydown", key); };
  }, [map, sel, setDraft, onCancel]); // eslint-disable-line

  const set = (patch: Partial<Draft>) => setDraft({ ...draft, ...patch });
  const isNew = !draft.id;
  return (
    <ToolPanel title={isNew ? "New zone" : `Edit zone`} onClose={onCancel}
      footer={<><button className="btn-ghost small" onClick={onCancel}>Cancel</button>
        <button className="btn-primary small" disabled={busy} onClick={onSave}>{busy ? "Saving…" : "Save zone"}</button></>}>
      <div className="zt-form">
        <label>Name<input value={draft.name} onChange={(e) => set({ name: e.target.value })} maxLength={60} placeholder="e.g. Nadi depot" autoFocus /></label>
        {isNew && (staff || companies.length > 1) && (
          <label>Who sees it
            <select value={draft.scope === "global" ? "__global" : draft.scope === "multi" ? "__multi" : draft.org_id}
              onChange={(e) => e.target.value === "__global" ? set({ scope: "global" })
                : e.target.value === "__multi" ? set({ scope: "multi", org_ids: draft.org_ids ?? [] })
                : set({ scope: "company", org_id: e.target.value })}>
              {staff && <option value="__global">Global — every company</option>}
              {admin && <option value="__multi">Several companies (a copy for each)…</option>}
              {companies.map((o) => <option key={o.id} value={o.id}>{o.name} only</option>)}
            </select>
          </label>
        )}
        {isNew && draft.scope === "multi" && (
          <CompanyTicks orgs={allCompanies} value={draft.org_ids ?? []} onChange={(ids) => set({ org_ids: ids })}
            hint="Each ticked company gets its own copy it can edit. A company that already has a zone with this name is skipped." />
        )}
        {!isNew && <p className="sub">{draft.scope === "global" ? "Global zone — visible to every company." : "Company zone."}</p>}
        <div className="seg" role="group" aria-label="Shape">
          <button type="button" className={draft.kind === "circle" ? "on" : ""} onClick={() => { setSel(null); set({ kind: "circle" }); }}>Circle</button>
          <button type="button" className={draft.kind === "polygon" ? "on" : ""} onClick={() => set({ kind: "polygon" })}>Polygon</button>
        </div>
        {draft.kind === "circle" ? (<>
          <p className="sub">Click the map to place the centre. Drag the centre to move it, or the edge handle to resize.</p>
          <label>Radius (m)
            <span className="zt-radius">
              <input type="range" min={20} max={5000} step={10} value={Math.min(draft.radius, 5000)} onChange={(e) => set({ radius: Number(e.target.value) })} />
              <input type="number" min={20} max={100000} value={draft.radius} onChange={(e) => set({ radius: Math.max(20, Math.min(100000, Number(e.target.value) || 20)) })} />
            </span>
          </label>
        </>) : (<>
          <p className="sub">Click the map to add corners ({draft.polygon.length} so far). Drag any corner to move it; drag or click a small “+” handle to add a corner between two others. Select a corner and press Delete to remove it.</p>
          {draft.polygon.length >= 3 && <p className="sub">Area: {fmtArea(areaM2(draft.polygon))}</p>}
          <span className="zt-row">
            <button type="button" className="btn-ghost small" disabled={sel == null} onClick={removeSel}>Delete corner{sel != null ? ` ${sel + 1}` : ""}</button>
            <button type="button" className="btn-ghost small" disabled={!draft.polygon.length} onClick={() => { setSel(null); set({ polygon: draft.polygon.slice(0, -1) }); }}>Undo last</button>
            <button type="button" className="btn-ghost small" disabled={!draft.polygon.length} onClick={() => { setSel(null); set({ polygon: [] }); }}>Clear</button>
          </span>
        </>)}
        <label>Colour</label>
        <ColourPicker value={draft.color} onChange={(c) => set({ color: c ?? DISPLAY_COLOURS[0] })} used={usedColours} />
        {err && <div className="error small">{err}</div>}
      </div>
    </ToolPanel>
  );
}

// ---------------------------------------------------------------- V3.12.0: import + several companies (admins)
type BulkResult = { targets: { target: string; created: number; skipped: string[] }[]; created: number; skipped: number };
function bulkText(r: BulkResult) {
  const per = r.targets.map((t) => `${t.target}: ${t.created}${t.skipped.length ? ` (${t.skipped.length} skipped)` : ""}`).join(" · ");
  return `Added ${r.created} zone${r.created === 1 ? "" : "s"}${r.skipped ? `, skipped ${r.skipped} with a name already in use` : ""}. ${per}`;
}

function CompanyTicks({ orgs, value, onChange, hint }: { orgs: { id: string; name: string }[]; value: string[]; onChange: (ids: string[]) => void; hint?: string }) {
  const [q, setQ] = useState("");
  const shown = orgs.filter((o) => !q.trim() || o.name.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div className="zt-ticks">
      <div className="zt-ticks-head">
        <span className="sub">{value.length} of {orgs.length} companies</span>
        <button type="button" className="linklike" onClick={() => onChange(value.length === orgs.length ? [] : orgs.map((o) => o.id))}>{value.length === orgs.length ? "None" : "All"}</button>
      </div>
      {orgs.length > 8 && <input className="zt-search" placeholder="Search companies" value={q} onChange={(e) => setQ(e.target.value)} />}
      <ul>{shown.map((o) => (
        <li key={o.id}><label><input type="checkbox" checked={value.includes(o.id)}
          onChange={(e) => onChange(e.target.checked ? [...value, o.id] : value.filter((x) => x !== o.id))} /> {o.name}</label></li>
      ))}</ul>
      {hint && <p className="sub">{hint}</p>}
    </div>
  );
}

function ZoneImport({ map, orgs, onCancel, onDone }: {
  map: MLMap; orgs: { id: string; name: string }[]; onCancel: () => void; onDone: (r: BulkResult) => void;
}) {
  const [file, setFile] = useState<string | null>(null);
  const [items, setItems] = useState<(ImportZone & { on: boolean })[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [radius, setRadius] = useState(100);
  const [global, setGlobal] = useState(false);
  const [orgIds, setOrgIds] = useState<string[]>([]);
  const [colour, setColour] = useState<string>(DISPLAY_COLOURS[0]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // preview layer
  useEffect(() => {
    const empty = { type: "FeatureCollection" as const, features: [] };
    if (!map.getSource("tf-zimport")) map.addSource("tf-zimport", { type: "geojson", data: empty });
    if (!map.getLayer("tf-zimport-fill")) map.addLayer({ id: "tf-zimport-fill", type: "fill", source: "tf-zimport", paint: { "fill-color": ["get", "c"], "fill-opacity": 0.15 } });
    if (!map.getLayer("tf-zimport-line")) map.addLayer({ id: "tf-zimport-line", type: "line", source: "tf-zimport", paint: { "line-color": ["get", "c"], "line-width": 2, "line-dasharray": [3, 2] } });
    return () => { for (const id of ["tf-zimport-line", "tf-zimport-fill"]) if (map.getLayer(id)) map.removeLayer(id); if (map.getSource("tf-zimport")) map.removeSource("tf-zimport"); };
  }, [map]);
  useEffect(() => {
    const feats = items.filter((z) => z.on).map((z) => ({ ...previewFeature(z, radius), properties: { c: z.color ?? colour } }));
    (map.getSource("tf-zimport") as GeoJSONSource | undefined)?.setData({ type: "FeatureCollection", features: feats as any });
  }, [map, items, radius, colour]);

  async function pick(f: File | undefined) {
    if (!f) return;
    setErr(null); setFile(f.name); setItems([]); setWarnings([]);
    try {
      const r = await readZoneFile(f);
      setItems(r.zones.map((z) => ({ ...z, on: true })));
      setWarnings(r.warnings);
      const pts = r.zones.flatMap((z) => (z.kind === "circle" ? [z.center!] : z.polygon));
      if (pts.length) { const b = new LngLatBounds(pts[0], pts[0]); pts.forEach((p) => b.extend(p)); map.fitBounds(b, { padding: 80, maxZoom: 15, duration: 600 }); }
    } catch (e: any) { setErr(e?.message ?? "Couldn't read that file"); }
  }
  const chosen = items.filter((z) => z.on);
  const targets = (global ? 1 : 0) + orgIds.length;
  async function go() {
    setErr(null);
    if (!chosen.length) return setErr("Tick at least one zone.");
    if (!targets) return setErr("Choose Global or at least one company.");
    if (chosen.some((z) => !z.name.trim())) return setErr("Every zone needs a name.");
    setBusy(true);
    try {
      const zones = chosen.map((z) => z.kind === "circle"
        ? { name: z.name.trim(), color: z.color ?? colour, kind: "circle", center_lat: z.center![1], center_lng: z.center![0], radius_m: z.radius ?? radius }
        : { name: z.name.trim(), color: z.color ?? colour, kind: "polygon", polygon: z.polygon });
      const r = await orgApi<BulkResult>("POST", "geofences/bulk", { targets: { global, org_ids: orgIds }, zones });
      rememberColour(colour);
      onDone(r);
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  }
  const set = (key: string, patch: Partial<ImportZone & { on: boolean }>) => setItems((l) => l.map((z) => (z.key === key ? { ...z, ...patch } : z)));
  const points = items.some((z) => z.kind === "circle" && z.radius == null);

  return (
    <ToolPanel title="Import zones" onClose={onCancel}
      footer={<><button className="btn-ghost small" onClick={onCancel}>Cancel</button>
        <button className="btn-primary small" disabled={busy || !chosen.length || !targets} onClick={go}>
          {busy ? "Adding…" : `Add ${chosen.length} zone${chosen.length === 1 ? "" : "s"}${targets > 1 ? ` × ${targets}` : ""}`}</button></>}>
      <div className="zt-form zt-import">
        <label className="zt-file">File (KML, KMZ, GeoJSON or zipped Shapefile)
          <input type="file" accept=".kml,.kmz,.geojson,.json,.zip" onChange={(e) => pick(e.target.files?.[0])} />
        </label>
        <p className="sub">Coordinates must be WGS84 longitude/latitude (Google Earth, QGIS EPSG:4326, or our own export). Polygons become zones; points become circles.</p>
        {warnings.length > 0 && <details className="zt-warn"><summary>{warnings.length} note{warnings.length === 1 ? "" : "s"} about this file</summary><ul>{warnings.slice(0, 50).map((w, i) => <li key={i}>{w}</li>)}</ul></details>}
        {file && !items.length && !err && <p className="sub">No zones found in {file}.</p>}
        {items.length > 0 && <>
          <div className="zt-ticks-head"><b>{chosen.length} of {items.length} zones</b>
            <button type="button" className="linklike" onClick={() => setItems((l) => l.map((z) => ({ ...z, on: chosen.length !== items.length })))}>{chosen.length === items.length ? "None" : "All"}</button></div>
          <ul className="zt-imp-list">{items.map((z) => (
            <li key={z.key}>
              <input type="checkbox" checked={z.on} onChange={(e) => set(z.key, { on: e.target.checked })} aria-label={`Include ${z.name}`} />
              <i className="zone-dot" style={{ background: z.color ?? colour }} />
              <input className="zt-imp-name" value={z.name} maxLength={60} onChange={(e) => set(z.key, { name: e.target.value })} aria-label="Zone name" />
              <span className="sub">{z.kind === "circle" ? `circle ${z.radius ?? radius} m` : `${z.polygon.length} pts`}</span>
            </li>
          ))}</ul>
          {points && <label>Radius for points without one (m)
            <input type="number" min={20} max={100000} value={radius} onChange={(e) => setRadius(Math.max(20, Math.min(100000, Number(e.target.value) || 100)))} /></label>}
          <label>Colour (when the file has none)</label>
          <ColourPicker value={colour} onChange={(c) => setColour(c ?? DISPLAY_COLOURS[0])} />
          <label className="zt-show"><input type="checkbox" checked={global} onChange={(e) => setGlobal(e.target.checked)} /> Global — every company sees them</label>
          <span className="sub">and/or add a copy for each of these companies:</span>
          <CompanyTicks orgs={orgs} value={orgIds} onChange={setOrgIds}
            hint="Names already used by that company (or by Global zones) are skipped — rename them above to add them anyway." />
        </>}
        {err && <div className="error small">{err}</div>}
      </div>
    </ToolPanel>
  );
}
